import * as React from 'react';
import classnames from 'classnames';

import { formatAsElementId } from './Utils';
import { FormComponent } from './Form';
import ErrorBlock from './ErrorBlock';

import { Binding, Result } from 'core/infrastructure';
import { cloneExcept } from '../../utils';

interface RadioButtonGroupProps<T> {
	linkTo: Binding.PropertyPath<T>;
	name: string;
	onValueChanged?: (value: any) => void;
	boundValue?: any;
	formId?: string;
}

interface RadioButtonGroupState {
	value?: any;
	errorMsg?: string;
}

export class RadioButtonGroup<T> extends React.Component<React.PropsWithChildren<RadioButtonGroupProps<T>>, RadioButtonGroupState> implements FormComponent {

	constructor(props: RadioButtonGroupProps<T>) {
		super(props);

		this.state = { value: props.boundValue };
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	getValue(): Result<any> {
		this.setState({ errorMsg: '' });
		return Result.ok(this.state.value);
	}

	showErrors(error: string[]): void {
		this.setState({
			errorMsg: error.join(' ')
		});
	}

	buildChildren(children: any): any {
		return React.Children.map(children, (child: any) => {

			if (!React.isValidElement<RadioButtonProps<T>>(child)) {
				return child;
			}

			const isRadioButton = typeof child.props.matchValue !== 'undefined';

			const newProps: any = child.props.children
				? { children: this.buildChildren(child.props.children) }
				: {};

			if (isRadioButton) {
				const matchValue = child.props.matchValue;
				newProps.checked = this.state.value === matchValue;
				newProps.name = this.props.name;
				newProps.onChange = () => {
					this.setState({ value: matchValue });
					this.props.onValueChanged?.(matchValue);
				};
			}

			return React.cloneElement(child as any, newProps);
		});
	}

	render() {
		const children = this.buildChildren(this.props.children);
		const classes = classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg });

		return <div id={formatAsElementId(this.getPath(), this.props.formId || '')} className={classes}><ErrorBlock msg={this.state.errorMsg} />{children}</div>;
	}
}

interface RadioButtonProps<T> extends React.HTMLProps<RadioButtonProps<T>> {
	matchValue: any;
	label?: string;
	checked?: boolean;
}

export class RadioButton<T> extends React.Component<RadioButtonProps<T>, EmptyState> {

	constructor(props: RadioButtonProps<T>) {
		super(props);
	}

	render() {
		const inputProps: any = cloneExcept(this.props, ['errorMsg', 'label', 'matchValue', 'formId', 'children']);
		const classes = classnames({ 'radio abc-radio': true, 'active': this.props.checked });
		const inputId = `radio-button-${this.props.matchValue}-${this.props.name}`;

		const subcontrolsStyle: React.CSSProperties = {
			paddingLeft: 40,
			marginTop: 5,
			marginBottom: -10,
			visibility: this.props.checked ? 'visible' : 'hidden'
		};

		const subcontrols = React.Children.count(this.props.children)
			? <div className='col-sm-12' style={subcontrolsStyle}>{this.props.children}</div>
			: null;

		return <div>
			<div className='col-sm-12'>
				<div className={classes}>
					<input id={inputId} type='radio' {...inputProps} />
					<label htmlFor={inputId}>{this.props.label || ''}</label>
				</div>
			</div>
			{subcontrols}
		</div>;
	}
}
export type RadioButtonGroupCtor<T> = new (props: RadioButtonGroupProps<T>) => RadioButtonGroup<T>;
export type RadioButtonCtor<T> = new (props: RadioButtonProps<T>) => RadioButton<T>;
